import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";

import AdminProfile from "../user/AdminProfile";
import Loader from "../layout/Loader";
import SalesChart from "./SalesChart";
import { useLazyGetDashboardSalesQuery } from "../../redux/api/orderApi";

const Dashboard = () => {
  const [startDate, setStartDate] = useState(() => new Date(new Date().setDate(1)));
  const [endDate, setEndDate] = useState(new Date());

  const [getDashboardSales, { error, isLoading, data }] =
    useLazyGetDashboardSalesQuery();

  // Fetch sales when dates change
  const fetchSales = useCallback(() => {
    if (startDate && endDate) {
      getDashboardSales({
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
      });
    }
  }, [startDate, endDate, getDashboardSales]);

 // Memoize chart data to prevent unnecessary re-renders
 const chartData = useMemo(() => data?.sales || [], [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.message);
    if (!data) fetchSales();
  }, [error, data, fetchSales]);

  if (isLoading) return <Loader />;

 

  return (
    <>
      <Helmet>
        <title>Dashboard</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <motion.div
        className="w-full mx-auto  py-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <AdminProfile>
          <h1 className="text-2xl md:text-3xl font-playfair text-[#1c1917] mb-6">
            Dashboard
          </h1>

          {/* Date Filters */}
          <div className="flex flex-col md:flex-row md:items-end gap-4 mb-8">
            <div className="flex flex-col">
              <label className="text-sm font-sourcesans text-stone-600 mb-1">
                Start Date
              </label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                className="form-control"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-sm font-sourcesans text-stone-600 mb-1">
                End Date
              </label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                className="form-control"
              />
            </div>
            <button
              onClick={fetchSales}
              className="bg-[#BC3F4A] hover:bg-[#a3323b] text-white px-5 py-2 rounded-md font-sourcesans transition"
            >
              Fetch
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/70 border border-stone-200 rounded-lg p-4 shadow-sm">
              <h2 className="text-lg font-playfair text-stone-800">
                {data?.totalNumOrders ?? 0} orders
              </h2>
            </div>
            <div className="bg-white/70 border border-stone-200 rounded-lg p-4 shadow-sm">
              <h2 className="text-lg font-playfair text-stone-800">
                ${data?.totalSales ?? 0} in sales
              </h2>
            </div>
          </div>

          {/* Sales Chart */}
          <div className="bg-white/70 border border-stone-200 rounded-lg p-4 shadow-sm">
            <SalesChart salesData={chartData} />
          </div>
        </AdminProfile>
      </motion.div>
    </>
  );
};

export default Dashboard;
